import { FileText, Loader2, Upload, X } from 'lucide-react';
import { useCallback, useState } from 'react';
import { type UploadFolder, uploadService } from '@/services/upload.service';

// Allowed image file types and their MIME types
const ALLOWED_IMAGE_TYPES = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/webp': ['.webp'],
} as const;

// Default max file size: 2MB in bytes
const DEFAULT_MAX_FILE_SIZE = 2 * 1024 * 1024;

interface ImageUploaderProps {
  /** The S3 key stored in database */
  value?: string;
  /** Called with S3 key for storage (use displayUrl for showing the image) */
  onChange: (key: string | undefined) => void;
  folder: UploadFolder;
  entityId?: string;
  label?: string;
  accept?: string;
  className?: string;
  /** Display URL (full CloudFront URL) - use this for img src */
  displayUrl?: string;
  /** Whether this is a document upload (shows file icon instead of image preview) */
  isDocument?: boolean;
  /** Maximum file size in bytes (default: 2MB) */
  maxFileSize?: number;
  /** Whether to validate file type strictly for images (default: false for backward compatibility) */
  validateImageType?: boolean;
  /** Minimum recommended width for quality display */
  recommendedMinWidth?: number;
  /** Minimum recommended height for quality display */
  recommendedMinHeight?: number;
}

// Helper function to format file size for display
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Helper function to validate file type
function isValidImageType(file: File): boolean {
  const allowedMimeTypes = Object.keys(ALLOWED_IMAGE_TYPES);
  if (allowedMimeTypes.includes(file.type)) {
    return true;
  }
  // Fallback: check file extension
  const extension = `.${file.name.split('.').pop()?.toLowerCase()}`;
  const allowedExtensions: string[] = Object.values(ALLOWED_IMAGE_TYPES).flat();
  return allowedExtensions.includes(extension);
}

// Get allowed extensions as a readable string
function getAllowedExtensions(): string {
  return Object.values(ALLOWED_IMAGE_TYPES).flat().join(', ');
}

export function ImageUploader({
  value,
  onChange,
  folder,
  entityId,
  label = 'Upload Image',
  accept = 'image/*',
  className = '',
  displayUrl,
  isDocument = false,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  validateImageType = false,
  recommendedMinWidth,
  recommendedMinHeight,
}: ImageUploaderProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  // Track the display URL for newly uploaded files
  const [localDisplayUrl, setLocalDisplayUrl] = useState<string | undefined>(undefined);

  // Helper to check image dimensions
  const checkImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
    return new Promise((resolve) => {
      const img = new window.Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
        URL.revokeObjectURL(img.src);
      };
      img.onerror = () => {
        resolve({ width: 0, height: 0 });
        URL.revokeObjectURL(img.src);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  // Use displayUrl prop if provided, otherwise use localDisplayUrl for newly uploaded files
  // If value looks like a full URL (from API in edit mode), use it directly as display URL
  const isValueAUrl = value?.startsWith('http://') || value?.startsWith('https://');
  const currentDisplayUrl = displayUrl || localDisplayUrl || (isValueAUrl ? value : undefined);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      // Reset the input value so the same file can be selected again after an error
      e.target.value = '';

      setError(null);
      setWarning(null);

      // Validate file type for images (when validateImageType is enabled)
      if (validateImageType && !isValidImageType(file)) {
        setError(`Invalid file type. Allowed formats: ${getAllowedExtensions()}`);
        return;
      }

      // Validate file size
      if (file.size > maxFileSize) {
        setError(
          `File size exceeds ${formatFileSize(maxFileSize)}. Selected file is ${formatFileSize(file.size)}.`
        );
        return;
      }

      // Check image dimensions for quality warning (only for non-document images)
      if (
        !isDocument &&
        (recommendedMinWidth || recommendedMinHeight) &&
        file.type.startsWith('image/')
      ) {
        const { width, height } = await checkImageDimensions(file);
        if (width > 0 && height > 0) {
          const isBelowWidth = recommendedMinWidth && width < recommendedMinWidth;
          const isBelowHeight = recommendedMinHeight && height < recommendedMinHeight;
          if (isBelowWidth || isBelowHeight) {
            setWarning(
              `Low resolution image (${width}×${height}px). For best quality, use at least ${recommendedMinWidth || 0}×${recommendedMinHeight || 0}px. The image may appear blurry or distorted.`
            );
          }
        }
      }

      setIsUploading(true);

      try {
        const { key, fileUrl } = await uploadService.uploadFile(file, folder, entityId);
        // Store the key in form data, and track the display URL locally
        onChange(key);
        setLocalDisplayUrl(fileUrl);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Upload failed');
      } finally {
        setIsUploading(false);
      }
    },
    [
      folder,
      entityId,
      onChange,
      maxFileSize,
      validateImageType,
      isDocument,
      recommendedMinWidth,
      recommendedMinHeight,
      checkImageDimensions,
    ]
  );

  const handleRemove = useCallback(() => {
    onChange(undefined);
    setLocalDisplayUrl(undefined);
    setWarning(null);
  }, [onChange]);

  // Helper to get filename from URL or key
  const getFilename = (urlOrKey: string): string => {
    const parts = urlOrKey.split('/');
    return parts[parts.length - 1] || 'Document';
  };

  return (
    <div className={className}>
      {label && (
        <span className="mb-[8px] block text-[14px] font-medium text-[#181818]">{label}</span>
      )}

      {value && currentDisplayUrl ? (
        <div className="relative inline-block">
          {isDocument ? (
            <a
              href={currentDisplayUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex h-[120px] w-[160px] flex-col items-center justify-center rounded-[8px] border border-[#e5e5e5] bg-[#f5f5f5] transition-colors hover:bg-[#eee]"
            >
              <FileText size={32} className="mb-[8px] text-[#055135]" />
              <span className="max-w-[140px] truncate px-[8px] text-[11px] text-[#646464]">
                {getFilename(currentDisplayUrl)}
              </span>
            </a>
          ) : (
            <img
              src={currentDisplayUrl}
              alt="Uploaded"
              className="h-[120px] w-[160px] rounded-[8px] border border-[#e5e5e5] object-cover"
            />
          )}
          <button
            type="button"
            onClick={handleRemove}
            className="absolute -right-[8px] -top-[8px] flex h-[24px] w-[24px] items-center justify-center rounded-full bg-[#d4183d] text-white shadow-md transition-colors hover:bg-[#b91c1c]"
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        <label className="flex h-[120px] w-[160px] cursor-pointer flex-col items-center justify-center rounded-[8px] border-2 border-dashed border-[#e5e5e5] bg-[#fafafa] transition-colors hover:border-[#055135] hover:bg-[#f5f5f5]">
          {isUploading ? (
            <Loader2 size={24} className="animate-spin text-[#055135]" />
          ) : (
            <>
              {isDocument ? (
                <FileText size={24} className="mb-[8px] text-[#8d8c8f]" />
              ) : (
                <Upload size={24} className="mb-[8px] text-[#8d8c8f]" />
              )}
              <span className="text-[12px] text-[#8d8c8f]">Click to upload</span>
            </>
          )}
          <input
            type="file"
            accept={accept}
            onChange={handleFileChange}
            disabled={isUploading}
            className="hidden"
          />
        </label>
      )}

      {error && <p className="mt-[4px] text-[12px] text-[#d4183d]">{error}</p>}
      {warning && <p className="mt-[4px] text-[12px] text-[#b45309]">{warning}</p>}
    </div>
  );
}
